import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type React from "react";
import type { DataGridHandle } from "../components/dataGrid/dataGrid.tsx";
import type { ButtonClickInfo } from "../components/dataGrid/cellRenderer.tsx";

export type GridDialogState =
  | { kind: "create" }
  | { kind: "edit"; id: number }
  | null;

export interface UseGridEditDialogOptions {
  /** Grid to refresh on save — pass the same ref you give to `<DataGrid ref=…>`. */
  gridRef?: React.RefObject<DataGridHandle | null>;

  /**
   * Column name to read for row-click edit dispatch. Default `"id"`.
   * e.g. merchandiser grid uses `"iKioskLoginID"`.
   */
  idColumn?: string;

  /**
   * URL query param for bookmark-compat (`?edit=<id>` opens edit on mount).
   * Default `"edit"`. Pass `null` to disable URL handoff entirely.
   */
  urlParam?: string | null;
}

export interface UseGridEditDialogReturn {
  dialog: GridDialogState;

  // Imperative openers / closers
  openCreate: () => void;
  openEdit: (id: number) => void;
  close: () => void;

  /** Closes dialog + refreshes grid. Pass straight to the editor as `onSaved`. */
  onSaved: () => void;

  // Pre-bound DataGrid props — wire directly into <DataGrid …>:
  handleRowSelect: (row: unknown[], colIndex: Record<string, number>) => void;
  handleButtonClick: (info: ButtonClickInfo) => void;
}

/**
 * Dialog-over-grid state + URL handoff + grid-refresh glue, per AGENTS.md
 * § Dialog-over-grid pattern + § URL param handoff.
 *
 * Returned `handleRowSelect` / `handleButtonClick` are ready to drop straight
 * into `<DataGrid onRowSelect=… onButtonClick=… />` for the common "click row
 * or Edit button → open edit modal" flow. Callers needing filtered dispatch
 * (e.g. Delete button → AlertDialog, only Edit → this hook) pass a wrapping
 * handler and call `openEdit(id)` themselves.
 *
 * Example:
 * ```tsx
 * const gridRef = useRef<DataGridHandle | null>(null);
 * const editor = useGridEditDialog({ gridRef, idColumn: "id" });
 *
 * <DataGrid
 *   ref={gridRef}
 *   name="thingGrid"
 *   onRowSelect={editor.handleRowSelect}
 *   onButtonClick={editor.handleButtonClick}
 *   toolbarExtra={<button onClick={editor.openCreate}>+ New</button>}
 * />
 * {editor.dialog && (
 *   <ThingEditor
 *     id={editor.dialog.kind === "edit" ? editor.dialog.id : 0}
 *     onClose={editor.close}
 *     onSaved={editor.onSaved}
 *   />
 * )}
 * ```
 */
export function useGridEditDialog(options?: UseGridEditDialogOptions): UseGridEditDialogReturn {
  const gridRef = options?.gridRef;
  const idColumn = options?.idColumn ?? "id";
  const urlParam = options?.urlParam === undefined ? "edit" : options.urlParam;

  const [dialog, setDialog] = useState<GridDialogState>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // URL handoff on mount:
  //   `?edit=<positive int>` → open edit modal for that id
  //   `?new=1` → open create modal
  // Both params are stripped (replace:true keeps history clean) so a back/forward
  // doesn't re-trigger the dialog. `?new=1` lets the modal-gallery (and any
  // deep-link) open the create flow directly without an extra grid-page click.
  useEffect(() => {
    if (urlParam == null) return;
    const raw = searchParams.get(urlParam);
    const newRaw = searchParams.get("new");
    let dirty = false;
    if (raw != null) {
      const id = Number(raw);
      if (Number.isFinite(id) && id > 0) setDialog({ kind: "edit", id });
      searchParams.delete(urlParam);
      dirty = true;
    } else if (newRaw != null) {
      setDialog({ kind: "create" });
      searchParams.delete("new");
      dirty = true;
    }
    if (dirty) setSearchParams(searchParams, { replace: true });
  // run once on mount — the urlParam, searchParams setter do not need to re-fire.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = useCallback(() => setDialog({ kind: "create" }), []);
  const openEdit = useCallback((id: number) => setDialog({ kind: "edit", id }), []);
  const close = useCallback(() => setDialog(null), []);

  const onSaved = useCallback(() => {
    setDialog(null);
    gridRef?.current?.refresh();
  }, [gridRef]);

  const handleRowSelect = useCallback(
    (row: unknown[], colIndex: Record<string, number>) => {
      // Case-insensitive column lookup. Grid column names come from the server
      // and may not match the caller's casing exactly (legacy `useSearchEditState`
      // did the same fallback).
      let colIdx = colIndex[idColumn];
      if (colIdx == null) {
        const lower = idColumn.toLowerCase();
        for (const key in colIndex) {
          if (key.toLowerCase() === lower) { colIdx = colIndex[key]; break; }
        }
      }
      if (colIdx == null) return;
      const id = Number(row[colIdx]);
      if (Number.isFinite(id) && id > 0) setDialog({ kind: "edit", id });
    },
    [idColumn],
  );

  const handleButtonClick = useCallback((info: ButtonClickInfo) => {
    const id = Number(info.rowData[0]);
    if (Number.isFinite(id) && id > 0) setDialog({ kind: "edit", id });
  }, []);

  return {
    dialog,
    openCreate,
    openEdit,
    close,
    onSaved,
    handleRowSelect,
    handleButtonClick,
  };
}
